import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 執事の接遇指針（route=/standards/hospitality）=====
// デザイン土台: 紺#1a2942 / 真鍮金#a38e65 / BTN-08 / 紺帯番号バンド / 白リッチカード / 細罫エディトリアル行
// /butler/become と同型セクションの使い回しはしない（構成を変えた新バリエーション）
// 事実正典: 創立1890年（明治23年）/ 法人設立2025年2月19日 / 代表理事 石井裕一（三代目頭取）/
//   資格は初級Associate・中級Professional・上級Masterの3段階+専門資格
// 創作した年号・数値・実績・違反件数・罰則は書かない

export default function StandardsHospitalityPage() {
  useEffect(() => {
    updateSEO(seoData['/standards/hospitality']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '基準・指針', href: '/standards' },
    { label: '執事の接遇指針' }
  ];

  const BTN08_ON_LIGHT =
    "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY =
    "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const boundaryAxes = [
    {
      en: 'Timing',
      ja: '時機の見極め',
      text: '必要が言葉になる手前で備えることと、相手がまだ求めていない段階で踏み込むことのあいだには、細い線があります。場の呼吸を読み、一歩手前で止まる勇気も接遇の一部です。'
    },
    {
      en: 'Presence',
      ja: '存在感の調整',
      text: '控えていることが安心につながる距離と、気配が重荷になる距離は紙一重です。視線・足音・声量を場面ごとに微調整し、「いてほしいのに目立たない」位置を保ちます。'
    },
    {
      en: 'Initiative',
      ja: '提案の節度',
      text: '先回りの提案は価値ですが、選択を奪う押しつけは奉仕ではありません。選択肢を静かに並べ、決定権を常に依頼者側へ残すことが過剰を防ぐ鍵になります。'
    }
  ];

  const keigoLayers = [
    {
      title: '依頼者への基調',
      body: '日常のやり取りでは、過度に硬い敬語の連発より、相手の好みに合わせた落ち着いた丁寧語を基調とします。親しみと品位のバランスは、家ごとの流儀を観察したうえで調整します。'
    },
    {
      title: '来客への基調',
      body: '初対面や公式な来訪では、型の通った敬語と取次の順序を優先します。相手の年齢・立場・訪問目的を踏まえ、丁寧さを保ちつつ冗長にならない簡潔さを心がけます。'
    },
    {
      title: '場に応じた切替',
      body: '私的な茶の間と、改まった応接、電話や文書では語彙の層が変わります。同じ相手でも場面が変われば距離感を更新する——その柔軟さが、機械的な敬語使用との違いです。'
    }
  ];

  const preferenceLedger = [
    {
      no: '01',
      title: '観察から仮説へ',
      text: '飲み物の残量、席の選び方、光や音への反応など、言葉にされない好みを観察し、仮説として心に留めます。断定せず、繰り返し現れる傾向だけを記録の候補に上げます。'
    },
    {
      no: '02',
      title: '確認と更新',
      text: '重要な好みは、適切な間を見て一言確認します。季節や体調、同行者によって変わる前提を忘れず、古いメモを盲信せず定期的に見直します。'
    },
    {
      no: '03',
      title: '共有の範囲',
      text: '記録は奉仕の精度を高めるためのものであり、好奇心のためのものではありません。共有する相手と範囲を最小限に定め、守秘の品位を最優先します。'
    },
    {
      no: '04',
      title: '引き継ぎの型',
      text: '休暇や交代の際は、好みの要点を簡潔な型で残します。感想や評価を混ぜず、事実と確認済みの希望だけを次の担当へ渡すことが信頼の連続性を支えます。'
    }
  ];

  const guestRanks = [
    {
      label: '公式・改まった来訪',
      points: [
        '玄関から案内までの動線と席次を事前に整える',
        '取次の順序と名乗らせ方を家の流儀に合わせる',
        '会話の間に不要な介入をせず、求められたときだけ補う'
      ]
    },
    {
      label: '親しい常連の来訪',
      points: [
        '過度な改まりで場を固めすぎない',
        'いつもの配慮（席・温度・飲み物）を静かに再現する',
        '打ち解けた空気でも守秘と節度の線は動かさない'
      ]
    },
    {
      label: '予期せぬ来訪・混在する場',
      points: [
        '初動で相手の立場を見極め、応対の層を切り替える',
        '依頼者の意向が不明なときは、一旦お預かりし確認する',
        '複数来客がいる場合は、誰にも偏らない公平な視線を保つ'
      ]
    }
  ];

  const declineSteps = [
    {
      en: 'Receive',
      ja: '受け止める',
      text: 'まず要望そのものを否定せず、伺った事実を短く復唱します。相手の意図が正しく届いていることを示すだけで、場の温度は大きく変わります。'
    },
    {
      en: 'Frame',
      ja: '理由を枠づける',
      text: '断る必要がある場合は、個人の好悪ではなく、家の方針・安全・準備状況など、相手が受け止めやすい枠で理由を示します。言い訳の長話は避けます。'
    },
    {
      en: 'Offer',
      ja: '代替を示す',
      text: '可能な範囲の別案を一つ以上提示します。「できない」で終わらせず、「こうすれば近づける」を静かに添えることが、接遇としての断り方です。'
    },
    {
      en: 'Confirm',
      ja: '合意を確認する',
      text: '代替案の選択を相手に委ね、決まった内容を復唱して閉じます。後から齟齬が出ないよう、必要なら簡潔な記録を残します。'
    }
  ];

  const criticismNotes = [
    '感情で反論せず、まず最後まで聴く。途中の正当化は信頼を削ります。',
    '事実・解釈・要望を分けて整理し、何が求められているかを明確にする。',
    'その場で直せることは速やかに直し、時間を要することは期限の目安を伝える。',
    '個人の尊厳を守りつつ、再発防止に必要な要点だけを関係者へ共有する。',
    '苦言を「敵」ではなく、奉仕の精度を上げる信号として受け取る姿勢を保つ。'
  ];

  const faqItems = [
    {
      q: '先回りと過剰の判断に迷ったときは、どうすればよいですか？',
      a: '迷った時点で一度立ち止まり、相手の選択権が残っているかを自問してください。備えは進めつつ、最終的な実行の可否を相手に確認する手順を挟むと、過剰に傾きにくくなります。家の流儀が既にある場合は、その型を優先します。'
    },
    {
      q: '敬語の硬さは、依頼者と来客でどの程度変えるべきですか？',
      a: '来客、とくに初対面や公式な場では型の通った丁寧さを優先し、依頼者との日常では相手の好みに合わせた落ち着いた層へ調整するのが基本です。どちらも「軽さ」と「冷たさ」の両極端を避け、場面ごとに更新する意識が大切です。'
    },
    {
      q: '好みの記録はどこまで残してよいのでしょうか？',
      a: '奉仕に直結し、確認済み、または繰り返し観察された事項に限定します。憶測や私的な評価、第三者に渡す必要のない機微は書きません。共有範囲を最小にし、引き継ぎ時も事実と希望の要点だけを渡すのが当協会の考え方です。'
    }
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '執事の接遇指針｜全日本執事協会が定める基準',
    description:
      '全日本執事協会が定める執事の接遇指針。依頼者と来客への接遇、先回りと過剰の境界、距離感と敬語、好みの把握と記録、来客の格に応じた応対、断り方と代替案、苦言の受け止め方。',
    author: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    publisher: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://japanbutlerassociation.com/standards/hospitality'
    }
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://japanbutlerassociation.com/' },
      { '@type': 'ListItem', position: 2, name: '基準・指針', item: 'https://japanbutlerassociation.com/standards' },
      { '@type': 'ListItem', position: 3, name: '執事の接遇指針', item: 'https://japanbutlerassociation.com/standards/hospitality' }
    ]
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* ヒーロー — 全幅ビルボード */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-std-hospitality-01.jpg"
          alt="静謐な空間で来客を迎える準備をする執事。接遇の品位を象徴する一枚"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              Hospitality Standards
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事の接遇指針</span>
              <span className="block text-[#d7b779]">依頼者と来客への基準</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、1890年（明治23年）の創立以来、日本における執事文化の継承に取り組んできました。代表理事・石井裕一（三代目頭取）のもと、本指針は認定団体である当協会が定める接遇の基準として、依頼者と来客の双方に向けた奉仕の考え方を示します。先回りの備えと過剰の境界、距離感と敬語、好みの把握、来客の格に応じた応対、断り方、苦言の受け止め方——現場で迷ったときに立ち返る座標です。
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-20 sm:space-y-24">

          {/* S1. 指針の位置づけ — 縦二段リード＋横三指標（becomeのステップ帯とは別型） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              I. Orientation
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              本指針が示す<span className="text-[#a38e65]">接遇の座標</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              <p>
                接遇とは、単なる笑顔や丁寧な言葉遣いの技法ではありません。依頼者が本来向き合うべき事柄に集中できるよう場を整え、来客にはその家の品位が過不足なく伝わるよう橋渡しをする——その静かな設計全体を指します。当協会は、Associate・Professional・Masterの三段階と専門資格を通じて技能を育てると同時に、現場判断の拠り所となる接遇の考え方を本指針にまとめています。
              </p>
              <p>
                以下では、先回りの奉仕と過剰の境界、距離感と敬語、依頼者の好みの把握と記録、来客の格に応じた応対、断り方と代替案、苦言の受け止め方を順に扱います。身だしなみや所作の細部は
                <a href="/standards/deportment" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">立ち居振る舞いの基準</a>
                と併せて参照し、基準全体の見取り図は
                <a href="/standards" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">基準・指針</a>
                から俯瞰できます。
              </p>
            </div>
            <div className="mt-10 grid sm:grid-cols-3 gap-px bg-[#e2ddd2] border border-[#e2ddd2]">
              {[
                { en: 'For the Principal', ja: '依頼者へ', sub: '日常を守り、決断の余白を残す' },
                { en: 'For the Guest', ja: '来客へ', sub: '家の流儀を過不足なく伝える' },
                { en: 'For the Craft', ja: '職能として', sub: '迷ったときに戻れる共通言語' }
              ].map((c) => (
                <div key={c.en} className="bg-[#fbfaf7] px-6 py-7">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em] mb-2">{c.en}</span>
                  <span className="block jba-display font-medium text-[#1a2942] text-lg mb-2">{c.ja}</span>
                  <span className="block font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">{c.sub}</span>
                </div>
              ))}
            </div>
          </section>

          {/* S2. 先回りと過剰 — 左本文＋右画像、下に三軸カード */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              II. Anticipation & Restraint
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              先回りの奉仕と過剰の境界
            </h2>
            <div className="grid lg:grid-cols-[1fr_340px] gap-10 items-start mb-10">
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                <p>
                  執事の接遇で最も尊ばれるもののひとつが、先回りの奉仕です。言葉になる前の必要を読み、滞りが表面化する手前で備える。しかしその誠実さは、一歩踏み外すと相手の領域への介入になります。境界を守るとは、何もしないことではなく、「備えは密かに、決定は相手に」を徹底することです。
                </p>
                <p>
                  過剰の兆候は、提案の頻度や声の大きさ、相手の沈黙を埋めようとする焦りに現れます。依頼者が考えている時間を奪っていないか、来客が望む以上の世話をしていないか——動作の前に一瞬の間を置く習慣が、品位ある先回りを支えます。
                </p>
              </div>
              <img
                src="/images/generated/jba-std-hospitality-02.jpg"
                alt="接遇の段取りを静かに確認する執事たちの打ち合わせ"
                className="w-full h-64 lg:h-[300px] object-cover border border-[#a38e65]/30"
              />
            </div>
            <div className="grid sm:grid-cols-3 gap-5">
              {boundaryAxes.map((p) => (
                <div key={p.en} className="bg-white border border-[#e2ddd2] shadow-[0_12px_40px_rgba(26,41,66,0.06)] px-6 py-7">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.08em] mb-2">{p.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-3">{p.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{p.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* S3. 距離感と敬語 — 紺帯＋三層パネル（becomeの二分割学習入口とは別型） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              III. Distance & Language
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              距離感と敬語
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              敬語は上下を誇示するための道具ではなく、相手と場を守るための距離の設計です。近すぎれば軽く、遠すぎれば冷たく響く。当協会が重視するのは、辞書的な正しさだけを追うことではなく、依頼者と来客、そして場面ごとに層を切り替える判断力です。
            </p>
            <div className="bg-[#1a2942] px-6 sm:px-8 py-4 mb-8 flex flex-wrap items-center gap-x-8 gap-y-2">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm">REGISTER</span>
              <span className="font-['Noto_Serif_JP',serif] text-[#f8f6f0]/85 text-sm tracking-[0.06em]">
                相手と場面で、言葉の層を静かに切り替える
              </span>
            </div>
            <div className="grid lg:grid-cols-3 gap-6">
              {keigoLayers.map((layer, idx) => (
                <div key={layer.title} className="border border-[#e2ddd2] bg-[#fbfaf7] p-7 flex flex-col">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl mb-3">{String(idx + 1).padStart(2, '0')}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{layer.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95]">{layer.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <img
                src="/images/generated/jba-std-hospitality-03.jpg"
                alt="礼節と言葉遣いの基礎を学ぶ研修の様子"
                className="w-full h-56 sm:h-72 object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S4. 好みの把握と記録 — 番号付き細罫リスト＋サイド画像 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IV. Preferences & Records
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              依頼者の好みの把握と記録
            </h2>
            <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-start">
              <div>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-6">
                  優れた接遇は、記憶に頼る属人的な勘だけに依存しません。観察し、確認し、必要な範囲で記録し、更新する——この循環があって初めて、休暇や交代のあとでも奉仕の質が途切れません。一方で、記録は最小限であるほど美しく、守秘の精神と常に一体です。
                </p>
                <div className="border-t border-[#1a2942]">
                  {preferenceLedger.map((item) => (
                    <div key={item.no} className="flex gap-5 py-5 border-b border-[#e2ddd2]">
                      <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl shrink-0 w-8">{item.no}</span>
                      <div>
                        <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{item.title}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
                <p className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm mb-3">Quiet memory</p>
                <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95] mb-6">
                  好みの把握は、相手を型にはめる作業ではありません。変化を前提に置き、今日の最善を静かに差し出すための備えです。メモより先に、目の前の人を見る姿勢を失わないでください。
                </p>
                <img
                  src="/images/gen2/std1-hospitality-preference-log.jpg"
                  alt="好みの記録と茶器を照合する執事の確認作業"
                  className="w-full h-52 object-cover border border-[#a38e65]/30"
                />
              </div>
            </div>
          </section>

          {/* S5. 来客の格に応じた応対 — 三列の縦カード（タイル型とは別） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              V. Guests by Occasion
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              来客の格に応じた応対
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-10">
              <p>
                来客対応に唯一の正解手順があるわけではありません。公式な訪問、親しい常連、予期せぬ来訪では、求められる改まりの層が異なります。重要なのは、相手を序列で見下したり持ち上げたりすることではなく、その家が示すべき敬意の形を、場面ごとに過不足なく再現することです。
              </p>
              <p>
                依頼者が同席しているか、取次のみか、複数の来客が入り混じるかによっても動線は変わります。事前に分かっている来訪では席次・飲み物・退出の合図までを備え、不明な点は推測で埋めず確認の手順を挟みます。
              </p>
            </div>
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              {guestRanks.map((g) => (
                <div key={g.label} className="bg-white border border-[#e2ddd2] shadow-[0_12px_40px_rgba(26,41,66,0.06)] p-7">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-4 pb-4 border-b border-[#e2ddd2]">{g.label}</h3>
                  <ul className="space-y-3">
                    {g.points.map((line) => (
                      <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[14px] text-[#333] leading-[1.85]">
                        <span className="text-[#a38e65] shrink-0 mt-0.5">▸</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <img
              src="/images/generated/jba-std-hospitality-05.jpg"
              alt="来客応対の動線と段取りを現場で確認する執事"
              className="w-full h-56 sm:h-72 object-cover border border-[#a38e65]/30"
            />
          </section>

          {/* S6. 断り方と代替案 — 横四ステップ（becomeの三ステップとも等級とも別） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VI. Decline with Care
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              断り方と代替案の示し方
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              接遇の品位は、願いを叶える場面だけでなく、叶えない場面でこそ試されます。無碍に否定するのでもなく、曖昧に引き延ばすのでもなく、相手の意図を受け止めたうえで、可能な道筋を差し出す。断り方そのものが、信頼を積む行為になり得ます。
            </p>
            <ol className="grid sm:grid-cols-2 lg:grid-cols-4 border-t-2 border-[#1a2942]">
              {declineSteps.map((s) => (
                <li key={s.en} className="border-b sm:border-b lg:border-b-0 lg:border-r last:border-r-0 border-[#e2ddd2] px-5 py-6">
                  <span className="block font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.12em] mb-2">{s.en}</span>
                  <span className="block jba-display font-medium text-[#1a2942] text-lg mb-3">{s.ja}</span>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.text}</p>
                </li>
              ))}
            </ol>
            <div className="mt-10 bg-[#fbfaf7] border border-[#e2ddd2] p-7 sm:p-8">
              <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95]">
                代替案は、相手を説得するための材料ではありません。選択の余地を残すための贈り物です。用意できる案が限られるときほど、言葉を短くし、トーンを下げ、決定を急かさない間を大切にしてください。依頼者の方針と矛盾する要望には、個人の判断で抱え込まず、確認の経路を踏むことが職能としての節度です。
              </p>
            </div>
          </section>

          {/* S7. 苦言の受け止め方 — 紺パネル＋リスト＋画像 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VII. Receiving Criticism
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              苦言の受け止め方
            </h2>
            <div className="bg-[#1a2942] p-8 sm:p-10 lg:p-12">
              <div className="grid lg:grid-cols-[1fr_300px] gap-10 items-start">
                <div>
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] mb-8">
                    苦言は、接遇の失敗を告げる音であると同時に、次の精度を上げるための信号です。防衛の言葉が先に出るほど、相手の信頼は遠ざかります。まず聴き、整理し、直せることを直し、必要な共有だけを行う——この順序を守ることが、個人の感情を超えた職能の応答です。
                  </p>
                  <ul className="space-y-4">
                    {criticismNotes.map((line) => (
                      <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[15px] text-[#f8f6f0] leading-[1.85]">
                        <span className="text-[#a38e65] shrink-0">—</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <img
                  src="/images/generated/jba-std-hospitality-06.jpg"
                  alt="落ち着いた佇まいで場を受け止める上級執事の姿勢"
                  className="w-full h-56 lg:h-[340px] object-cover border border-[#a38e65]/40"
                />
              </div>
            </div>
            <p className="mt-8 font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              苦言の内容がチームや家全体の運用に関わる場合は、個人の反省で閉じてしまわず、手順や引き継ぎの型へ還元します。Master Butlerが示す統括の視点も、こうした現場の信号を制度に戻すところにあります。資格の段階にかかわらず、受け止める力はすべての接遇者に求められます。
            </p>
          </section>

          {/* S8. FAQ */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VIII. Questions
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくある問い
            </h2>
            <div className="border-t-2 border-[#1a2942]">
              {faqItems.map((item) => (
                <div key={item.q} className="border-b border-[#e2ddd2] py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3 flex gap-3">
                    <span className="text-[#a38e65] font-['Playfair_Display',serif] shrink-0">Q</span>
                    <span>{item.q}</span>
                  </h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] pl-8">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* S9. 関連導線 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IX. Related Paths
            </span>
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              関連する基準と学びへ
            </h3>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                接遇指針は、単独で完結するものではありません。立ち居振る舞いの基準とあわせて身体の型を整え、資格制度を通じて判断力を段階的に育てることで、現場での再現性が生まれます。全日本執事協会は、1890年（明治23年）から続く系譜のうえで、いまの暮らしにふさわしい奉仕の座標を示し続けます。
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
                <a href="/standards" className={BTN08_ON_NAVY}>
                  基準・指針の一覧
                </a>
                <a href="/standards/deportment" className={BTN08_ON_NAVY}>
                  立ち居振る舞いの基準
                </a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>
                  資格制度を見る
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
